import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { Plus, Pencil, Trash2, LogOut, ExternalLink, GripVertical } from "lucide-react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { SortableArea } from "@/components/admin/Sortable";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, fileUrl, formatApiError } from "@/lib/api";
import { SermonForm } from "@/components/admin/SermonForm";
import { TeamPanel } from "@/components/admin/TeamPanel";
import { SettingsPanel } from "@/components/admin/SettingsPanel";
import { SchedulePanel } from "@/components/admin/SchedulePanel";
import { AnnouncementsPanel } from "@/components/admin/AnnouncementsPanel";
import { TextPanel } from "@/components/admin/TextPanel";
import { DonationsPanel } from "@/components/admin/DonationsPanel";
import { AccountPanel } from "@/components/admin/AccountPanel";
import { ActivityPanel } from "@/components/admin/ActivityPanel";
import { ApprovalsPanel } from "@/components/admin/ApprovalsPanel";
import { PagesPanel } from "@/components/admin/PagesPanel";
import { GalleryPanel } from "@/components/admin/GalleryPanel";
import { VisualEditor } from "@/components/admin/VisualEditor";
import { PublishBar } from "@/components/admin/PublishBar";
import { BulkSermons } from "@/components/admin/BulkSermons";
import { PastorsPanel } from "@/components/admin/PastorsPanel";
import { useSite } from "@/context/SiteContext";
import { AREAS, ROLE_LABEL, canRead, canWrite, isSuper } from "@/lib/roles";

const TABS = [AREAS[0], ["pastors", "Pastors"], ...AREAS.slice(1), ["approvals", "Approvals"], ["team", "Team"], ["activity", "Activity"], ["account", "My Account"]];
const allowed = (u, k) => (k === "pastors" ? canRead(u, "sermons") : ["team", "activity"].includes(k) ? isSuper(u) : k === "approvals" ? u?.role !== "viewer" : k === "account" ? true : canRead(u, k));

const SermonRow = ({ s, canDelete, onEdit, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: s.id, disabled: !canDelete });
  const details = useSite().settings?.sermon_details === true;
  return (
  <div ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition }} data-testid={`admin-sermon-row-${s.id}`} className={`flex items-center gap-4 border-b border-[#F4EEE2]/10 py-4 ${isDragging ? "relative z-10 rounded-xl bg-[#131C2E] shadow-xl" : ""}`}>
    {canDelete && <button type="button" data-testid={`admin-sermon-drag-${s.id}`} {...attributes} {...listeners} aria-label="Drag to reorder" className="cursor-grab touch-none rounded-lg p-1.5 text-[#B7BFCC] hover:text-[#D4AF37] active:cursor-grabbing"><GripVertical className="h-4 w-4" /></button>}
    <div className="h-16 w-14 shrink-0 overflow-hidden rounded-lg bg-[#1C2D42]">
      {s.cover_url && <img src={fileUrl(s.cover_url)} alt="" className="h-full w-full object-cover" />}
    </div>
    <div className="min-w-0 flex-1">
      <p className="truncate font-ui font-semibold text-[#F4EEE2]">{s.title_en}</p>
      <p data-testid={`admin-sermon-meta-${s.id}`} className="truncate text-xs text-[#B7BFCC]">{[s.speaker, details && s.date, details && (s.audio_url ? "Audio ✓" : "No audio yet"), s.video_url && "Video ✓", s.attachments?.length && `${s.attachments.length} file(s)`].filter(Boolean).join(" · ")}</p>
    </div>
    <button data-testid={`admin-edit-sermon-${s.id}`} onClick={onEdit} className="rounded-full p-2 text-[#B7BFCC] hover:text-[#D4AF37]" aria-label="Edit"><Pencil className="h-4 w-4" /></button>
    {canDelete && <button data-testid={`admin-delete-sermon-${s.id}`} onClick={onDelete} className="rounded-full p-2 text-[#B7BFCC] hover:text-[#F2A08A]" aria-label="Delete"><Trash2 className="h-4 w-4" /></button>}
  </div>
  );
};

export default function Admin() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState(null);
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/sermons").then((r) => setItems(Array.isArray(r.data) ? r.data : [])).catch(() => setItems([]));
  useEffect(() => {
    load();
  }, []);

  if (user === null) return <div className="min-h-screen" />;
  if (!user) return <Navigate to="/admin/login" replace />;
  const isMaster = isSuper(user);
  const tabs = TABS.filter(([k]) => allowed(user, k));
  const current = tab && allowed(user, tab) ? tab : tabs[0][0];
  const writable = ["team", "account", "activity", "approvals"].includes(current) || canWrite(user, current === "pastors" ? "sermons" : current);
  const speakers = [...new Set(items.map((s) => s.speaker?.trim()).filter(Boolean))].sort();

  const reorder = async (ids) => {
    const prev = items;
    setItems(ids.map((id) => items.find((s) => s.id === id)));
    try { await api.put("/sermons/order", { ids }); toast.success("Order saved"); }
    catch (e) { setItems(prev); toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const del = async (s) => {
    if (!window.confirm(`Delete "${s.title_en}"?`)) return;
    await api.delete(`/sermons/${s.id}`).then((r) => { if (r.data?.pending) toast("Delete sent for approval"); else toast.success("Deleted"); load(); }).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  };

  return (
    <main data-testid="admin-dashboard" className={`mx-auto min-h-screen px-5 py-10 md:px-10 ${current === "design" ? "max-w-[1680px]" : "max-w-6xl"}`}>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <img src="/images/logo-round.png" alt="Awa In Kelet logo" className="h-11 w-11 rounded-full ring-1 ring-[#D4AF37]/50" />
          <div>
            <h1 className="font-display text-3xl font-semibold text-[#F4EEE2]">Team Portal</h1>
            <p data-testid="admin-user-info" className="text-xs text-[#B7BFCC]">{user.name} · <span className="uppercase text-[#D4AF37]">{ROLE_LABEL[user.role] || user.role}</span></p>
          </div>
        </div>
        <div className="flex gap-2">
          <Link to="/" data-testid="admin-view-site" className="flex items-center gap-1.5 rounded-full border border-[#F4EEE2]/15 px-4 py-2 font-ui text-xs text-[#F4EEE2]"><ExternalLink className="h-3.5 w-3.5" /> View site</Link>
          <button data-testid="admin-logout-btn" onClick={logout} className="flex items-center gap-1.5 rounded-full border border-[#F4EEE2]/15 px-4 py-2 font-ui text-xs text-[#F4EEE2]"><LogOut className="h-3.5 w-3.5" /> Sign out</button>
        </div>
      </div>

      {user.can_publish && current !== "design" && <PublishBar key={current} onReview={() => setTab("design")} />}

      <div className="mt-10 flex flex-wrap items-center justify-between gap-4 border-b border-[#F4EEE2]/10 pb-4">
        <div className="flex flex-wrap gap-2">
          {tabs.map(([k, label]) => (
            <button key={k} data-testid={`admin-tab-${k}`} onClick={() => setTab(k)} className={`rounded-full px-4 py-2 font-ui text-sm ${current === k ? "bg-[#D4AF37] font-semibold text-[#0B111E]" : "text-[#F4EEE2]/80 hover:bg-[#F4EEE2]/5"}`}>
              {label}
            </button>
          ))}
        </div>
        {current === "sermons" && writable && (
          <button data-testid="admin-add-sermon-btn" onClick={() => setEditing({})} className="flex items-center gap-2 rounded-full bg-[#F4EEE2] px-5 py-2.5 font-ui text-sm font-semibold text-[#0B111E] hover:bg-[#D4AF37]">
            <Plus className="h-4 w-4" /> New message
          </button>
        )}
      </div>

      {!writable && (
        <p data-testid="admin-readonly-notice" className="mt-4 rounded-xl border border-[#D4AF37]/30 bg-[#D4AF37]/10 px-4 py-3 font-ui text-sm text-[#F3D27A]">
          View only — you can look at this area but not change it.
        </p>
      )}
      <fieldset disabled={!writable} className="mt-4 min-w-0 disabled:opacity-80">
        {current === "pastors" && <div className="mt-4"><PastorsPanel writable={writable} /></div>}
        {current === "sermons" && writable && <BulkSermons speakers={speakers} onDone={load} />}
        {current === "sermons" && writable && items.length > 1 && <p className="mt-6 text-xs text-[#9AA4B4]">Drag the handle on any message to change the order. The website shows them in this order.</p>}
        {current === "sermons" && (
          <SortableArea ids={items.map((s) => s.id)} onReorder={reorder}>
            {items.map((s) => <SermonRow key={s.id} s={s} canDelete={writable} onEdit={() => setEditing(s)} onDelete={() => del(s)} />)}
          </SortableArea>
        )}
        {current !== "sermons" && (
          <div className="mt-6">
            {current === "announcements" && <AnnouncementsPanel isMaster={writable} />}
            {current === "text" && <TextPanel />}
            {current === "schedule" && <SchedulePanel />}
            {current === "settings" && <SettingsPanel />}
            {current === "gifts" && <DonationsPanel />}
            {current === "team" && isMaster && <TeamPanel me={user} />}
            {current === "activity" && isMaster && <ActivityPanel />}
            {current === "approvals" && <ApprovalsPanel isSuper={isMaster} />}
            {current === "pages" && <PagesPanel canBuiltIn={canWrite(user, "settings")} />}
            {current === "gallery" && <GalleryPanel />}
            {current === "design" && <VisualEditor />}
            {current === "account" && <AccountPanel user={user} />}
          </div>
        )}
      </fieldset>

      {editing && <SermonForm open speakers={speakers} initial={editing.id ? editing : null} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </main>
  );
}
